import React from 'react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Plus, Lock, CalendarOff, CalendarClock } from 'lucide-react';
import ShiftChip from './ShiftChip';
import { fmtDayNumber, fmtTime, hoursLabel, levelStyle, money, WEEKDAY_LABELS, isoWeekday } from '../../../lib/scheduleFormat';

// The dense admin week: kitchen availability as a background band, bookings as
// solid blocks on top of it, shifts as rows inside those blocks.
const WeekGrid = ({ week, onAddShift, onEditShift, onAddBooking, onEditBooking, onUsualHours }) => {
  const days = week.days;
  const canTakeKitchenShift = (day) => day.bookings.length > 0;

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white" data-testid="week-grid">
      <div className="min-w-[1000px]">
        {/* Day header */}
        <div className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] border-b border-gray-200 bg-gray-50">
          <div className="p-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {week.timezone}
          </div>
          {days.map((day) => {
            const style = levelStyle(day.coverage?.level);
            return (
              <div key={day.date} className="border-l border-gray-200 p-3" data-testid={`grid-day-${day.date}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {WEEKDAY_LABELS[isoWeekday(day.date)]}
                  </span>
                  {day.coverage && (
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${style.dot}`}
                      title={`${style.label}`}
                      data-testid={`grid-coverage-dot-${day.date}`}
                    />
                  )}
                </div>
                <p className="text-lg font-bold text-gray-900">{fmtDayNumber(day.date)}</p>
                <p className="text-[11px] text-gray-500">
                  {day.order_count} order{day.order_count === 1 ? '' : 's'}
                </p>
              </div>
            );
          })}
        </div>

        {/* Kitchen availability + bookings */}
        <div className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] border-b border-gray-200">
          <div className="bg-gray-50/60 p-3">
            <p className="text-sm font-semibold text-gray-900">Kitchen hours</p>
            <p className="text-xs text-gray-500">Access band, then what you booked</p>
          </div>
          {days.map((day) => (
            <div key={day.date} className="border-l border-gray-200 p-2"
              data-testid={`grid-bookings-${day.date}`}>
              {day.availability.length > 0 ? (
                <div className="mb-2 rounded bg-slate-100 px-2 py-1 text-[11px] text-slate-700"
                  data-testid={`grid-availability-${day.date}`}>
                  {day.availability.map((w, i) => (
                    <span key={i} className="block">
                      {fmtTime(w.start_time)}–{fmtTime(w.end_time)}
                      {w.source !== 'weekly' && <span className="ml-1 italic">override</span>}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mb-2 rounded bg-gray-100 px-2 py-1 text-[11px] text-gray-500"
                  data-testid={`grid-noaccess-${day.date}`}>
                  No kitchen access
                </p>
              )}

              <div className="space-y-1">
                {day.bookings.map((booking) => (
                  <button
                    key={booking.id}
                    type="button"
                    onClick={() => onEditBooking(booking, day)}
                    className="w-full rounded-md border border-amber-300 bg-amber-100 px-2 py-1.5 text-left text-xs text-amber-900 hover:bg-amber-200"
                    data-testid={`grid-booking-${booking.id}`}
                  >
                    <span className="block font-medium">
                      {fmtTime(booking.start_time)}–{fmtTime(booking.end_time)}
                    </span>
                    <span className="block opacity-90">
                      {hoursLabel(booking.hours)} · {money(booking.cost)}
                    </span>
                    <span className="mt-0.5 flex flex-wrap gap-1">
                      <Badge variant="outline" className="h-4 border-amber-400 bg-white/70 px-1 text-[10px]">
                        {booking.status}
                      </Badge>
                      {booking.source === 'pattern' && (
                        <Badge variant="outline" className="h-4 border-amber-400 bg-white/70 px-1 text-[10px]"
                          title="Booked by the standing weekly pattern">
                          standing
                        </Badge>
                      )}
                      {booking.conflict && (
                        <Badge variant="outline" className="h-4 border-red-300 bg-white/70 px-1 text-[10px] text-red-700">
                          conflict
                        </Badge>
                      )}
                    </span>
                  </button>
                ))}
              </div>

              {day.availability.length > 0 && (
                <Button
                  variant="ghost" size="sm"
                  className="mt-1 h-7 w-full justify-start px-1 text-xs text-gray-500 hover:text-amber-700"
                  onClick={() => onAddBooking(day)}
                  data-testid={`grid-add-booking-${day.date}`}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Book hours
                </Button>
              )}
              {day.availability.length > 0 && day.bookings.length === 0 && onUsualHours && (
                <Button
                  variant="ghost" size="sm"
                  className="h-7 w-full justify-start px-1 text-xs text-gray-500 hover:text-amber-700"
                  title="Book what the standing weekly hours say for this day"
                  onClick={() => onUsualHours(day)}
                  data-testid={`grid-usual-hours-${day.date}`}
                >
                  <CalendarClock className="mr-1 h-3.5 w-3.5" /> Usual hours
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* One row per person, then the open-shift row */}
        {week.rows.map((row) => (
          <div
            key={row.user_id || 'open'}
            className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] border-b border-gray-100 last:border-b-0"
            data-testid={`grid-row-${row.user_id || 'open'}`}
          >
            <div className={`p-3 ${row.user_id ? '' : 'bg-slate-50'}`}>
              <p className="truncate text-sm font-medium text-gray-900">{row.name}</p>
              <p className="truncate text-[11px] text-gray-500">
                {row.user_id ? row.positions.join(' · ') : 'Unassigned — publishable, shows amber'}
              </p>
            </div>
            {days.map((day) => {
              const shifts = row.shifts.filter((s) => s.date === day.date);
              const drives = !row.user_id || row.positions.includes('driver');
              const kitchenOk = canTakeKitchenShift(day);
              const away = (row.time_off_dates || []).includes(day.date);
              const canAdd = !away && (kitchenOk || drives);
              return (
                <div
                  key={day.date}
                  className={`border-l border-gray-200 p-1.5 ${
                    away ? 'bg-violet-50' : kitchenOk ? '' : 'bg-gray-50/70'
                  }`}
                  data-testid={`grid-cell-${row.user_id || 'open'}-${day.date}`}
                >
                  {away && (
                    <p
                      className="mb-1 flex items-center gap-1 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800"
                      title={row.time_off_reason || 'Approved time off'}
                      data-testid={`grid-timeoff-${row.user_id}-${day.date}`}
                    >
                      <CalendarOff className="h-3 w-3" /> Off
                    </p>
                  )}
                  <div className="space-y-1">
                    {shifts.map((shift) => (
                      <ShiftChip key={shift.id} shift={shift} onClick={onEditShift} />
                    ))}
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    className="mt-1 h-7 w-full justify-center px-1 text-gray-400 hover:text-amber-700"
                    disabled={!canAdd}
                    title={
                      away
                        ? `Approved time off${row.time_off_reason ? ` — ${row.time_off_reason}` : ''}`
                        : canAdd ? 'Add a shift' : 'No kitchen hours booked on this day'
                    }
                    onClick={() => onAddShift(day, row)}
                    data-testid={`grid-add-shift-${row.user_id || 'open'}-${day.date}`}
                  >
                    {canAdd ? <Plus className="h-3.5 w-3.5" /> : <Lock className="h-3 w-3" />}
                  </Button>
                </div>
              );
            })}
          </div>
        ))}

        {/* Per-day totals */}
        <div className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] border-t border-gray-200 bg-gray-50">
          <div className="p-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Booked hours &amp; cost
          </div>
          {days.map((day) => (
            <div key={day.date} className="border-l border-gray-200 p-3 text-xs text-gray-700"
              data-testid={`grid-totals-${day.date}`}>
              <span className="block font-semibold text-gray-900">{hoursLabel(day.booked_hours)}</span>
              <span className="block">{money(day.booked_cost)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeekGrid;
